import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@otp/ui';
import {
  LinkOutline,
  ToolOutline,
  LearningCenterOutline,
  ChecklistOutline,
  UserOutline,
  CardLink,
  ResourcesContainedOutline,
} from '@otp/icons';
import { Wttp } from './wttp';
import { ContentWrapper } from '../common/wrapper/content/wrapper';
import { Actions, AppArea, Subjects } from 'src/graphql/generated/graphql';
import { useAuthContext } from 'src/context/auth.context';
import { getUserPrograms } from 'src/utils/user-data';
import { AdditionalResources } from './additional-resources';
import { useCan } from 'src/utils/can';
import QuickLinks from './quick-links';
import LearningCenter from './learning-center';
import Grid from '@mui/material/Grid';
import { RestaurantMap, RestaurantList } from './restaurant-selection-v2';
import { HomeContainedOutline } from '@otp/icons';
import { ProgramOptIn } from './program-opt-in';
import { ContactAdministrator } from './program-opt-in/contact-administrator';
import { MembersBreakdown } from './manage-otps-v2/members';
import { MembersInOrganisation } from './manage-otps-v2/otp-list/otp-list';
import { Stack } from '@mui/material';
import { Link } from '../common/link';
import { Routes } from '../common/router/constants';
import { MostRecentCard } from '../common/most/recent';
import { FoldersCard } from '../common/resources/folders';
import { BookmarksCard } from '../common/resources/bookmarks';
import { RecentResourcesCard } from '../common/resources/recent';

export const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const userPrograms = getUserPrograms(user);
  const { can } = useCan();

  const canReadUsers = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Users,
  });
  const canReadStores = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Stores,
  });
  const canReadIssues = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Issues,
  });
  const canReadWttp = can({
    action: Actions.Read,
    appArea: AppArea.Wttp,
    subject: Subjects.Wttp,
  });
  const canReadQuickLinks = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Lms,
  });
  const canReadAdditionalResources = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Common,
  });

  const canReadManageOTPs = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Users,
  });

  const canReadMost = can({
    action: Actions.Read,
    appArea: AppArea.Most,
    subject: Subjects.Most,
  });

  const canReadResources = can({
    action: Actions.Read,
    appArea: AppArea.Common,
    subject: Subjects.Resources,
  });

  // Add a permission check for learning center if needed
  const canAccessLearning = can({
    action: Actions.Read,
    appArea: AppArea.Lms,
    subject: Subjects.Curriculums,
  });

  if (
    !canReadUsers &&
    !canReadStores &&
    !canReadIssues &&
    !canReadWttp &&
    !canReadQuickLinks &&
    !canReadAdditionalResources &&
    !canReadManageOTPs &&
    !userPrograms.length
  ) {
    return (
      <ContentWrapper>
        <Grid container className="pt-0 md:pt-8">
          <Grid item xs={12}>
            <Card noBorder sx={{ p: 6 }} className="bg-white w-full md:w-auto">
              <Grid container alignItems="center" gap={2}>
                <UserOutline />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.program-opt-in.title')}
                </Typography>
              </Grid>

              <Grid container className="mt-4">
                <ContactAdministrator />
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </ContentWrapper>
    );
  }

  if (
    !canReadUsers &&
    !canReadStores &&
    !canReadIssues &&
    !canReadWttp &&
    !canReadQuickLinks &&
    !canReadAdditionalResources &&
    !canReadManageOTPs &&
    userPrograms.length
  ) {
    return (
      <ContentWrapper>
        <div className="flex gap-2 justify-center w-full pt-0 md:pt-8 flex-col md:flex-row">
          <Card
            noBorder
            sx={{ p: 6 }}
            className="bg-white flex flex-col w-full md:w-auto"
          >
            <div className="flex gap-3 items-center">
              <UserOutline />
              <Typography
                variant="body1"
                fontWeight="medium"
                fontFamily="Inter"
              >
                {t('my-organisation.program-opt-in.title')}
              </Typography>
            </div>

            <div className="flex flex-1 mt-4 gap-2 md:gap-1 flex-col md:flex-row">
              <ProgramOptIn />
            </div>
          </Card>
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <Grid container spacing={2} className="pt-0 md:pt-8">
        {/* Learning Center Section */}
        {canAccessLearning && (
          <Grid container item xs={12} md={6}>
            <Grid item xs={12} md={12}>
              <Card
                noBorder
                sx={{
                  p: 6,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                className="bg-white"
                linkPosition="top"
              >
                <Grid container alignItems="center" gap={6} className="h-full">
                  <Grid container alignItems="center" gap={2}>
                    <LearningCenterOutline />
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      fontFamily="Inter"
                    >
                      {t('my-organisation.learning-center.title')}
                    </Typography>
                  </Grid>
                  <Grid container className="flex-1">
                    <LearningCenter />
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Most Recent Section */}
        {canReadMost && (
          <Grid item xs={12} md={6}>
            <div className="flex gap-2 justify-center w-full flex-col md:flex-row">
              <Card noBorder sx={{ p: 6 }} className="bg-white w-full ">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography
                    fontWeight="bold"
                    className="flex gap-2 items-center"
                  >
                    <ToolOutline />
                    {t('my-organisation.most.title')}
                  </Typography>
                  <Link
                    to={Routes.mostBase.path}
                    className="flex gap-2 items-center"
                  >
                    {t('common.go-to-overview')}
                    <CardLink />
                  </Link>
                </Stack>
                <div className="flex flex-1 mt-4 gap-2 md:gap-1 flex-col md:flex-row">
                  <MostRecentCard />
                </div>
              </Card>
            </div>
          </Grid>
        )}

        {/* Manage OTPs Section */}
        {canReadManageOTPs && (
          <Grid item xs={12} md={6}>
            <Card
              noBorder
              sx={{
                p: 6,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
              className="bg-white"
            >
              <Grid container gap={6} alignItems="center">
                <Grid container item alignItems="center" gap={2}>
                  <ChecklistOutline />
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.manage-otps-v2.title')}
                  </Typography>
                </Grid>
                <Grid container item spacing={3}>
                  <Grid item xs={12} md={7}>
                    <MembersInOrganisation />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <MembersBreakdown />
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}

        {/* Resources Section */}
        {canReadResources && (
          <Grid item xs={12} lg={9}>
            <div className="flex gap-2 justify-center w-full">
              <Card noBorder sx={{ p: 6 }} className="bg-white w-full">
                <Typography
                  fontWeight="bold"
                  className="flex gap-2 items-center"
                >
                  <ResourcesContainedOutline />
                  {t('my-organisation.resources.title')}
                </Typography>
                <div className="flex flex-1 mt-4 gap-2 md:gap-1 flex-col md:flex-row">
                  <FoldersCard />
                  <BookmarksCard />
                  <RecentResourcesCard />
                </div>
              </Card>
            </div>
          </Grid>
        )}

        {/* Restaurant Selection Section */}
        {canReadStores && (
          <Grid item xs={12} md={6}>
            <Card
              noBorder
              sx={{
                p: 6,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              className="bg-white"
              linkPosition="top"
            >
              <Grid container alignItems="center" gap={6}>
                <Grid container alignItems="center" gap={2}>
                  <HomeContainedOutline />
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.restaurant-selection')}
                  </Typography>
                </Grid>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <RestaurantList />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <RestaurantMap />
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        )}

        {/* WTTP Section */}
        {canReadWttp && (
          <Grid item xs={12} md={9}>
            <Card
              noBorder
              sx={{ p: 6 }}
              className="bg-white h-full"
              linkPosition="top"
            >
              <Grid container spacing={2} alignItems="center" gap={2}>
                <ChecklistOutline />
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  fontFamily="Inter"
                >
                  {t('my-organisation.wttp.title')}
                </Typography>
              </Grid>
              <Grid container className="flex-1 mt-4">
                <Wttp />
              </Grid>
            </Card>
          </Grid>
        )}

        {/* Quick Links Section */}
        {canReadQuickLinks && (
          <Grid item xs={12} md={3}>
            <Card
              noBorder
              sx={{ p: 6 }}
              className="bg-white h-full"
              linkPosition="top"
            >
              <Grid container alignItems="center" gap={2}>
                <LinkOutline />
                <Typography fontWeight="medium" fontFamily="Inter">
                  {t('my-organisation.quick-links.title')}
                </Typography>
              </Grid>
              <Grid container className="flex-1 mt-4">
                <QuickLinks />
              </Grid>
            </Card>
          </Grid>
        )}

        {/* Additional Resources Section */}
        {canReadAdditionalResources && (
          <Grid item xs={12}>
            <Card
              noBorder
              sx={{ p: 6 }}
              className="bg-white w-full md:w-auto"
              linkPosition="top"
            >
              <Grid container alignItems="center" gap={2}>
                <ToolOutline />
                <Typography fontWeight="medium" fontFamily="Inter">
                  {t('my-organisation.additional-resources.title')}
                </Typography>
              </Grid>
              <Grid container className="mt-4">
                <AdditionalResources />
              </Grid>
            </Card>
          </Grid>
        )}
      </Grid>
    </ContentWrapper>
  );
};
